import AuthService from "../../../../services/AuthService";
import Logger from "../../../../services/Logger";
import { consumeCredits } from "../_stubs/MobileCreditService";
import { SLIDING_CREDIT_APP_CODE } from "./creditConstants";
import { isConfigurationLocked } from "./subscriptionManager";

const PLAY_CREDIT_CHARGED_SESSION_KEY = "sliding_play_credit_charged_v1";
const MAX_CHARGED_SESSION_KEYS = 32;

function readChargedSessionKeys() {
    try {
        const raw = sessionStorage.getItem(PLAY_CREDIT_CHARGED_SESSION_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch {
        return new Set();
    }
}

function markChargedSessionKey(sessionKey) {
    const key = String(sessionKey || "").trim();
    if (!key) return;
    try {
        const set = readChargedSessionKeys();
        set.add(key);
        sessionStorage.setItem(
            PLAY_CREDIT_CHARGED_SESSION_KEY,
            JSON.stringify([...set].slice(-MAX_CHARGED_SESSION_KEYS)),
        );
    } catch {
        /* ignore */
    }
}

/** True for grid/difficulty pairs that require credits (not free 4×4 Easy). */
export function isPremiumSlidingConfiguration(gridSize, difficulty) {
    return isConfigurationLocked(gridSize, difficulty, false);
}

export function buildPremiumPlayCreditSessionKey(slidingId, gridSize, difficulty) {
    return `${String(slidingId || "").trim()}|${gridSize}|${difficulty}`;
}

export function wasPremiumPlayCreditCharged(sessionKey) {
    return readChargedSessionKeys().has(String(sessionKey));
}

export function isInsufficientCreditError(error) {
    const msg = String(error?.message || "").toLowerCase();
    return msg.includes("insufficient");
}

/** Fresh wallet + legacy flags before charging (avoids stale React state on first tap). */
export async function resolvePremiumPlayCreditContext({ access, credits } = {}) {
    let creditBalance = Number(credits?.creditBalance) || 0;
    if (credits?.balanceLoading || !credits?.balanceLoaded) {
        const bal = await credits.refreshBalance();
        if (Number.isFinite(Number(bal))) {
            creditBalance = Number(bal);
        }
    }
    const hasCredits = creditBalance > 0;
    const hasLegacyUnlimitedAccess =
        !access?.loading &&
        access?.isAuthenticated &&
        (access?.hasStandardPlan || access?.isPremium) &&
        !hasCredits;
    return { hasCredits, hasLegacyUnlimitedAccess, creditBalance };
}

/**
 * Charge the credit wallet for premium play (skip legacy unlimited Standard / RC access).
 * @param {{ gridSize: number, difficulty: string, hasLegacyUnlimitedAccess?: boolean, hasCredits?: boolean }} params
 */
export function shouldChargeCreditsForPremiumPlay({
    gridSize,
    difficulty,
    hasLegacyUnlimitedAccess = false,
    hasCredits = false,
}) {
    if (!isPremiumSlidingConfiguration(gridSize, difficulty)) {
        return false;
    }
    if (hasCredits) {
        return true;
    }
    if (hasLegacyUnlimitedAccess) {
        return false;
    }
    return true;
}

/**
 * POST /home/credit/consume — one credit when entering a premium sliding play session.
 * @returns {Promise<{ creditBalance: number, creditsConsumed: number } | null>}
 */
export async function consumeCreditForPremiumPlayStart({ slidingId, gridSize, difficulty } = {}) {
    if (!isPremiumSlidingConfiguration(gridSize, difficulty)) {
        return null;
    }

    const authed = await AuthService.isAuthenticated();
    if (!authed) {
        return null;
    }

    const sid = String(slidingId || "").trim();
    if (!sid) {
        return null;
    }
    const featureUsed = `${SLIDING_CREDIT_APP_CODE}:${sid}`;

    try {
        const result = await consumeCredits({ featureUsed, creditsConsumed: 1 });
        return {
            creditBalance: result.creditBalance,
            creditsConsumed: result.creditsConsumed,
            featureUsed: result.featureUsed,
        };
    } catch (e) {
        Logger.warn("consumeCreditForPremiumPlayStart", e);
        throw e;
    }
}

/**
 * Charge before navigating to play (home / library). Skips resume and free-tier configs.
 * Server idempotency prevents double-charging if PlayPage also calls consume.
 */
export async function chargePremiumPlayCreditOnEntry({
    slidingId,
    gridSize,
    difficulty,
    hasLegacyUnlimitedAccess = false,
    hasCredits = false,
    applyCreditBalance,
    skip = false,
} = {}) {
    if (skip) {
        return { charged: false, skipped: true, reason: "resume" };
    }

    if (
        !shouldChargeCreditsForPremiumPlay({
            gridSize,
            difficulty,
            hasLegacyUnlimitedAccess,
            hasCredits,
        })
    ) {
        return { charged: false, skipped: true, reason: "not_premium_or_legacy" };
    }

    const sid = String(slidingId || "").trim();
    if (!sid) {
        return { charged: false, skipped: true, reason: "missing_sliding_id" };
    }

    const sessionKey = buildPremiumPlayCreditSessionKey(sid, gridSize, difficulty);
    if (wasPremiumPlayCreditCharged(sessionKey)) {
        return { charged: false, skipped: true, reason: "already_charged_session" };
    }

    const result = await consumeCreditForPremiumPlayStart({ slidingId: sid, gridSize, difficulty });
    if (!result) {
        return { charged: false, skipped: true, reason: "consume_null" };
    }

    markChargedSessionKey(sessionKey);

    if (typeof applyCreditBalance === "function" && Number.isFinite(Number(result.creditBalance))) {
        applyCreditBalance(result.creditBalance);
    }

    return { charged: true, ...result };
}

/** @deprecated Use {@link consumeCreditForPremiumPlayStart} — credits are charged on play entry. */
export async function consumeCreditForCompletedPremiumPuzzle(params) {
    return consumeCreditForPremiumPlayStart(params);
}
